"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
interface ArtistSubmission {
  id: string;
  imageUrl: string;
  mediaMimeType: string;
  title?: string | undefined;
  createdAt: number;
  drop?: ApiDrop | undefined;
}
interface User {
  id: string;
  handle?: string | null | undefined;
  level: number;
  cic: number;
  rep: number;
  pfp?: string | null | undefined;
  active_main_stage_submission_ids?: string[] | undefined;
}
interface UseUserArtSubmissionsReturn {
  submissions: ArtistSubmission[];
  submissionCount: number;
  isLoading: boolean;
  error: string | null;
}
interface UseSubmissionDropsReturn {
  submissionsWithDrops: ArtistSubmission[];
  isLoading: boolean;
  error: string | null;
}
const fetchDrop = (dropId: string) => commonApiFetch<ApiDrop>({
  endpoint: `drops/${dropId}`
});
export const useSubmissionDrops = submissions => {
  const $ = _c(15);
  let t0;
  if ($[0] !== submissions) {
    t0 = submissions.map(_temp);
    $[0] = submissions;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      queries: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const dropQueries = useQueries(t1);
  let t2;
  if ($[4] !== dropQueries || $[5] !== submissions) {
    let t3;
    if ($[7] !== dropQueries) {
      t3 = (submission_0, index) => {
        const dropQuery = dropQueries[index];
        return {
          ...submission_0,
          drop: dropQuery?.data
        };
      };
      $[7] = dropQueries;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = submissions.map(t3).filter(_temp2);
    $[4] = dropQueries;
    $[5] = submissions;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const submissionsWithDrops = t2;
  let t3;
  if ($[9] !== dropQueries) {
    t3 = dropQueries.some(_temp3);
    $[9] = dropQueries;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const isLoading = t3;
  const hasError = dropQueries.some(_temp4);
  const error = hasError ? "Failed to load drop data" : null;
  let t4;
  if ($[11] !== error || $[12] !== isLoading || $[13] !== submissionsWithDrops) {
    t4 = {
      submissionsWithDrops,
      isLoading,
      error
    };
    $[11] = error;
    $[12] = isLoading;
    $[13] = submissionsWithDrops;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export const useUserArtSubmissions = user => {
  const $ = _c(20);
  user?.handle;
  user?.id;
  const authorKey = user?.handle ?? user?.id;
  let t0;
  if ($[0] !== authorKey) {
    t0 = [QueryKey.PROFILE_DROPS, authorKey, "art-submissions"];
    $[0] = authorKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0 as const;
  let t1;
  if ($[2] !== user) {
    t1 = async () => {
      if (!user?.handle && !user?.id) {
        return [];
      }
      const params = {
        limit: "50",
        author: user.handle ?? user.id,
        drop_type: ApiDropType.Participatory
      };
      const result = await commonApiFetch({
        endpoint: "/drops",
        params
      });
      return result;
    };
    $[2] = user;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!user;
  let t3;
  if ($[4] !== queryKey || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey,
      queryFn: t1,
      enabled: t2,
      staleTime: 300000
    };
    $[4] = queryKey;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data: drops,
    isLoading,
    error
  } = useQuery(t3);
  let t4;
  bb0: {
    if (!drops || !user) {
      let t5;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = [];
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      t4 = t5;
      break bb0;
    }
    let t5;
    if ($[9] !== user.active_main_stage_submission_ids) {
      t5 = user.active_main_stage_submission_ids || [];
      $[9] = user.active_main_stage_submission_ids;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    const activeSubmissionIds = t5;
    let t6;
    if ($[11] !== activeSubmissionIds || $[12] !== drops) {
      let t7;
      if ($[14] !== activeSubmissionIds) {
        t7 = drop_0 => activeSubmissionIds.includes(drop_0.id);
        $[14] = activeSubmissionIds;
        $[15] = t7;
      } else {
        t7 = $[15];
      }
      t6 = drops.filter(_temp5).filter(t7).map(_temp6).filter(_temp7);
      $[11] = activeSubmissionIds;
      $[12] = drops;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    const realSubmissions = t6;
    t4 = realSubmissions;
  }
  const submissions = t4;
  const t5 = error?.message || null;
  let t6;
  if ($[16] !== isLoading || $[17] !== submissions || $[18] !== t5) {
    t6 = {
      submissions,
      submissionCount: submissions.length,
      isLoading,
      error: t5
    };
    $[16] = isLoading;
    $[17] = submissions;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
function _temp(submission) {
  return {
    queryKey: [QueryKey.DROP, {
      drop_id: submission.id
    }],
    queryFn: () => fetchDrop(submission.id),
    enabled: !!submission.id,
    staleTime: 300000
  };
}
function _temp2(submission_1) {
  return submission_1.drop;
}
function _temp3(query) {
  return query.isLoading;
}
function _temp4(query_0) {
  return query_0.error;
}
function _temp5(drop) {
  return drop.drop_type === ApiDropType.Participatory;
}
function _temp6(drop_1) {
  const firstMedia = drop_1.parts?.[0]?.media?.[0];
  const imageUrl = firstMedia?.url || "";
  const mediaMimeType = firstMedia?.mime_type || "image/jpeg";
  return {
    id: drop_1.id,
    imageUrl,
    mediaMimeType,
    title: drop_1.title || undefined,
    createdAt: drop_1.created_at
  };
}
function _temp7(submission) {
  return submission.imageUrl;
}